import { useState } from "react";
import type { Player } from "@/lib/types";
import { CoachModal } from "./CoachModal";
import { TkticSelect } from "@/components/ui/TkticSelect";

/**
 * Header-level "Link Account" entry point — Coach > Roster.
 *
 * `LinkAccountModal` links an account to ONE known player, so it
 * needs a player up front. The per-row link action already has one;
 * the header button does not. This modal supplies the missing step:
 * pick a roster player, then continue into `LinkAccountModal`.
 *
 * It is intentionally a thin picker — it does NOT do the link write
 * itself. On "Continue" it calls `onPick(player)`, and the page
 * hands that player straight to `LinkAccountModal` (the same modal
 * the per-row action opens), so there is exactly one link flow.
 */

export type LinkAccountPickerModalProps = {
  players: Player[];
  onPick: (player: Player) => void;
  onClose: () => void;
};

export function LinkAccountPickerModal({
  players,
  onPick,
  onClose,
}: LinkAccountPickerModalProps) {
  const [playerId, setPlayerId] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleContinue = () => {
    const player = players.find((p) => p.id === playerId);
    if (!player) {
      setError("Pick a player to link an account to.");
      return;
    }
    onPick(player);
  };

  return (
    <CoachModal
      title="Link an account"
      onClose={onClose}
      data-testid="roster-link-picker-modal"
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
            Player
          </span>
          <TkticSelect
            value={playerId}
            onChange={(next) => {
              setPlayerId(next);
              setError(null);
            }}
            data-testid="roster-link-picker-player"
            ariaLabel="Player"
            options={[
              {
                value: "",
                label:
                  players.length === 0
                    ? "No players yet"
                    : "Select a player…",
              },
              ...players.map((p) => ({
                value: p.id,
                label: p.jersey_number
                  ? `#${p.jersey_number} · ${p.display_name}`
                  : p.display_name,
              })),
            ]}
          />
        </label>

        {error ? (
          <p
            className="text-destructive font-mono text-xs"
            data-testid="roster-link-picker-error"
          >
            {error}
          </p>
        ) : null}

        <button
          type="button"
          onClick={handleContinue}
          disabled={players.length === 0}
          data-testid="roster-link-picker-continue"
          className="rounded bg-accent px-4 py-2 text-sm font-display uppercase tracking-wide text-white hover:bg-accent-hover disabled:bg-bg-card disabled:text-ink-muted transition-colors"
        >
          Continue
        </button>
      </div>
    </CoachModal>
  );
}
